import { formatDate } from '@/lib/format'
import type { Customer, CustomerValidationStatus, Note } from '@/api/types'

export type CustomerValidationState = 'needs_review' | 'corrected' | 'confirmed' | 'approved' | 'verified_unknown' | 'waiting'

export const VALIDATION_STATUS_VALUES: CustomerValidationStatus[] = ['manual', 'verified', 'pending']

export const VALIDATION_STATUS_LABELS: Record<CustomerValidationStatus, string> = {
  manual: 'Behöver kontroll',
  verified: 'Verifierad',
  pending: 'Väntar'
}

const VALIDATION_REASON_LABELS: Record<string, string> = {
  PNR_INVALID: 'Personnumret är ogiltigt',
  PNR_AGE: 'Åldern ligger utanför 18–100 år',
  SAMORDNINGSNUMMER: 'Samordningsnummer slås inte upp automatiskt',
  PNR_NOT_FOUND: 'Personnumret finns inte i folkbokföringen',
  NUSVAR_UNREACHABLE: 'Nusvar gick inte att nå',
  NUSVAR_INCOMPLETE: 'Nusvar saknade efternamn eller adress (skyddad identitet?)',
  NAME_MISMATCH: 'Namnet stämmer inte med folkbokföringen',
  ADDRESS_CONFLICT: 'Rätt person, men annan adress än folkbokföringen'
}

const UNKNOWN_REASON_LABEL = 'Uppgifterna kunde inte bekräftas mot folkbokföringen'

const CHANGE_LINE_MARKER = ' → '

const APPROVAL_LINE_PREFIX = 'Validering: '

export function getValidationReasonLabel(reason: string | null): string {
  if (reason === null) {
    return UNKNOWN_REASON_LABEL
  }

  return VALIDATION_REASON_LABELS[reason] ?? UNKNOWN_REASON_LABEL
}

function getNotes(customer: Customer): Note[] {
  return customer.notes ?? []
}

function findRegisterVerificationNote(customer: Customer): Note | undefined {
  return getNotes(customer).find((note) => note.user_id === null)
}

function hasApprovalLine(note: Note): boolean {
  return note.body.split('\n').some((line) => line.startsWith(APPROVAL_LINE_PREFIX))
}

function findApprovalNote(customer: Customer): Note | undefined {
  return getNotes(customer).find((note) => note.kind === 'change' && note.user_id !== null && hasApprovalLine(note))
}

function isNewerNote(candidate: Note | undefined, other: Note | undefined): boolean {
  if (candidate === undefined) {
    return false
  }

  if (other === undefined) {
    return true
  }

  return candidate.created_at > other.created_at
}

export function getCorrectedFieldCount(customer: Customer): number {
  const note = findRegisterVerificationNote(customer)

  if (note === undefined) {
    return 0
  }

  return note.body.split('\n').filter((line) => line.includes(CHANGE_LINE_MARKER)).length
}

export function getValidationState(customer: Customer): CustomerValidationState {
  if (customer.validation_status === 'manual') {
    return 'needs_review'
  }

  if (customer.validation_status === 'pending') {
    return 'waiting'
  }

  const registerNote = findRegisterVerificationNote(customer)
  const approvalNote = findApprovalNote(customer)

  if (registerNote === undefined && approvalNote === undefined) {
    return 'verified_unknown'
  }

  if (isNewerNote(approvalNote, registerNote)) {
    return 'approved'
  }

  const hasCorrections = registerNote?.kind === 'change' && getCorrectedFieldCount(customer) > 0

  if (hasCorrections) {
    return 'corrected'
  }

  return 'confirmed'
}

export function formatCorrectedFieldCount(count: number): string {
  if (count === 1) {
    return '1 uppgift'
  }

  return `${count} uppgifter`
}

function formatNoteDateSuffix(note: Note | undefined): string {
  if (note === undefined) {
    return ''
  }

  return ` ${formatDate(note.created_at)}`
}

export function getRegisterVerificationSummary(customer: Customer): string {
  const dateSuffix = formatNoteDateSuffix(findRegisterVerificationNote(customer))
  const correctedCount = getCorrectedFieldCount(customer)

  if (correctedCount === 0) {
    return `Verifierad mot folkbokföringen${dateSuffix}`
  }

  return `Verifierad mot folkbokföringen${dateSuffix} — ${formatCorrectedFieldCount(correctedCount)} rättades`
}

export function getApprovedSummary(customer: Customer): string {
  const note = findApprovalNote(customer)
  const name = note?.user_display_name ?? null
  const dateSuffix = formatNoteDateSuffix(note)

  if (name === null) {
    return `Godkänd manuellt${dateSuffix}`
  }

  return `Godkänd av ${name}${dateSuffix}`
}

export function isCustomerVerified(customer: Customer): boolean {
  return customer.validation_status === 'verified'
}
